import { useEffect, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import { fetchNotifications, markNotificationsRead } from "@/lib/api";
import { useWs } from "@/context/WsContext";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const { subscribe } = useWs();
  const navigate = useNavigate();

  const load = useCallback(() => fetchNotifications().then(setItems).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  // Poll every 30s + refresh on WS message
  useEffect(() => {
    const t = setInterval(load, 30000);
    const unsub = subscribe(() => load());
    return () => { clearInterval(t); unsub(); };
  }, [load, subscribe]);

  const unread = items.filter((n) => !n.read).length;

  const handleOpen = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try { await markNotificationsRead(); } catch {}
      setTimeout(load, 300);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 hover:text-primary transition-colors">
          <Bell className="w-5 h-5" strokeWidth={1.5}/>
          {unread > 0 && (
            <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] mono font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none border-foreground/20 w-80 max-h-96 overflow-y-auto">
        <DropdownMenuLabel className="overline">◆ Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator/>
        {items.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground text-center">No notifications</div>
        ) : (
          items.map((n) => (
            <button
              key={n.notification_id}
              data-testid={`notif-${n.notification_id}`}
              onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}
              className={`w-full text-left px-3 py-3 border-b border-foreground/10 hover:bg-muted ${!n.read ? "bg-primary/5" : ""}`}
            >
              <div className="text-sm font-medium line-clamp-2">{n.text}</div>
              <div className="mono text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
            </button>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
